// src/components/panel/InvoiceAnalytics.js
import React, { useState, useEffect } from 'react';
import { FileTextOutlined } from '@ant-design/icons';
import { Spin, DatePicker } from 'antd';
import { analyticsService } from '../../services/analyticsService';
import dayjs from 'dayjs';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

function InvoiceAnalytics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDate, setSelectedDate] = useState(isDemoMode ? dayjs('2026-01-13') : dayjs());
    const [datesWithInvoices, setDatesWithInvoices] = useState([]);

    useEffect(() => {
        loadAvailableDates();
    }, []);

    useEffect(() => {
        loadData();
    }, [selectedDate]);

    const loadAvailableDates = async () => {
        try {
            // Get actual dates with invoices from backend
            const response = await analyticsService.getInvoiceDates(90);
            const rawDates = response.data?.dates || [];
            
            // Convert ISO datetime strings to YYYY-MM-DD format
            const dates = rawDates.map(dateStr => {
                return dayjs(dateStr).format('YYYY-MM-DD');
            });
            
            setDatesWithInvoices(dates);
        } catch (err) {
            console.error('[InvoiceAnalytics] Error loading available dates:', err);
            // Fallback: enable last 30 days
            const dates = [];
            const today = dayjs();
            for (let i = 0; i < 30; i++) {
                dates.push(today.subtract(i, 'day').format('YYYY-MM-DD'));
            }
            setDatesWithInvoices(dates);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const response = await analyticsService.getAverageInvoiceValue(dateStr);
            setData(response.data || null);
            setError(null);
        } catch (err) {
            console.error('Error loading invoice analytics:', err);
            setError('Error al cargar análisis de facturas');
        } finally {
            setLoading(false);
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            setSelectedDate(date);
        }
    };

    const disabledDate = (current) => {
        if (!current) return false;
        // Disable future dates (demo ceiling = Jan 13 2026)
        const ceiling = isDemoMode ? dayjs('2026-01-13') : dayjs();
        if (current.isAfter(ceiling, 'day')) return true;
        // Disable dates without invoices (from backend)
        const dateStr = current.format('YYYY-MM-DD');
        return !datesWithInvoices.includes(dateStr);
    };

    if (loading) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <FileTextOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>ANÁLISIS DE FACTURAS</h2>
                </div>
                
                <div className='card-content-center'>
                    <Spin tip="Cargando análisis..." />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className='card'>
                <div className='card-inner'>
                    <FileTextOutlined className='card_icon' style={{ fontSize: '38px' }} />
                    <h2>ANÁLISIS DE FACTURAS</h2>
                </div>
                
                <div className='card-content-center' style={{ color: '#ff4d4f' }}>
                    {error}
                </div>
            </div>
        );
    }

    const totalInvoices = data?.TotalInvoices || 0;
    const totalSales = data?.TotalSales || 0;
    const avgValue = data?.AverageInvoiceValue || 0;
    const minValue = data?.MinInvoiceValue || 0;
    const maxValue = data?.MaxInvoiceValue || 0;
    const range = maxValue - minValue;

    // Calculate percentage position for min/max markers
    const minPosition = range > 0 ? 0 : 50;
    const maxPosition = range > 0 ? 100 : 50;

    return (
        <div className='card'>
            <div className='card-inner'>
                <FileTextOutlined className='card_icon' style={{ fontSize: '38px' }} />
                <h2>ANÁLISIS DE FACTURAS</h2>
            </div>
            
            <div style={{ marginTop: '12px', marginBottom: '16px' }}>
                <label style={{ fontSize: '0.9rem', color: '#595959', marginRight: '8px' }}>
                    Fecha:
                </label>
                <DatePicker 
                    value={selectedDate}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                    allowClear={false}
                    disabledDate={disabledDate}
                    style={{ width: '140px' }}
                />
            </div>

            <div className='invoice-analytics-content'>
                <div className='analytics-summary-row'>
                    <div className='analytics-summary-item'>
                        <div className='analytics-label'>Total Facturas</div>
                        <div className='analytics-value' style={{ color: '#1890ff' }}>{totalInvoices}</div>
                    </div>
                    <div className='analytics-summary-item'>
                        <div className='analytics-label'>Ventas Totales</div>
                        <div className='analytics-value' style={{ color: '#52c41a' }}>
                            ${totalSales.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </div>
                    </div>
                </div>

                <div className='analytics-average-section'>
                    <div className='analytics-average-label'>Promedio por Factura</div>
                    <div className='analytics-average-value'>
                        ${avgValue.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </div>
                    <div className='analytics-average-bar'>
                        <div className='analytics-average-fill' style={{ width: '75%' }}></div>
                    </div>
                </div>

                <div className='analytics-range-section'>
                    <div className='analytics-range-header'>
                        <span style={{ color: '#8c8c8c' }}>Mínima</span>
                        <span style={{ color: '#8c8c8c' }}>Máxima</span>
                    </div>
                    <div className='analytics-range-values'>
                        <span style={{ fontWeight: 600, color: '#ff4d4f' }}>
                            ${minValue.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                        <span style={{ fontWeight: 600, color: '#52c41a' }}>
                            ${maxValue.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                        </span>
                    </div>
                    <div className='analytics-range-bar'>
                        <div className='analytics-range-track'>
                            <div 
                                className='analytics-range-marker analytics-range-min' 
                                style={{ left: `${minPosition}%` }}
                            ></div>
                            <div 
                                className='analytics-range-marker analytics-range-max' 
                                style={{ left: `${maxPosition}%` }}
                            ></div>
                        </div>
                    </div>
                    <div className='analytics-range-info'>
                        Rango: ${range.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvoiceAnalytics;
